import Chip from "@mui/material/Chip";
import type { ChipProps } from "@mui/material/Chip";
import { Stack, Typography } from "@mui/material";
import { CustomCellDate, CrudLink } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { PagePaths } from "@/admin/shared/constants";
import type { SubscriptionRow } from "./types";

/** Причина неудачного продления у просроченной подписки. */
const RENEW_ERROR_LABELS: Record<string, string> = {
  noPaymentMethod: "Нет карты",
  chargeFailed: "Списание не прошло",
  insufficientFunds: "Не хватило средств",
  cardExpired: "Карта истекла",
  cardDeclined: "Банк отклонил списание",
};

/** Пустая ячейка: без длинного тире (правило текстов проекта). */
const EMPTY_CELL = "-";

/** Причина и номер попытки у просроченной подписки, иначе пустая ячейка. */
function renderPastDue(row: SubscriptionRow) {
  if (row.status !== "PAST_DUE") return EMPTY_CELL;
  const reason = row.renewErrorReason
    ? (RENEW_ERROR_LABELS[row.renewErrorReason] ?? row.renewErrorReason)
    : EMPTY_CELL;
  return (
    <Stack>
      <Typography variant="body2">{reason}</Typography>
      <Typography variant="body2" color="textSecondary">
        Попытка {row.renewAttempts}
      </Typography>
    </Stack>
  );
}

const statusChipConfig: Record<
  string,
  { label: string; color: ChipProps["color"] }
> = {
  ACTIVE: { label: "Активна", color: "success" },
  // Продление не прошло, идут повторы списания (спека viewer-money).
  PAST_DUE: { label: "Просрочена", color: "warning" },
  CANCELED: { label: "Отменена", color: "default" },
  EXPIRED: { label: "Истекла", color: "error" },
};

function renderStatus(status: string) {
  const { label, color } = statusChipConfig[status] ?? {
    label: status,
    color: "default" as const,
  };
  return <Chip label={label} color={color} size="small" />;
}

export const getColumnsConfig = (): ColumnDefinition<SubscriptionRow>[] => [
  { name: "id", label: "ID", options: { display: false, filter: false } },
  {
    name: "status",
    label: "Статус",
    options: {
      customBodyRender: (value: unknown) => renderStatus(value as string),
    },
  },
  {
    name: "nextRenewal",
    label: "Следующее списание",
    options: {
      setCellHeaderProps: () => ({ style: { width: 170 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
  {
    name: "pastDue",
    label: "Просрочка",
    options: {
      sort: false,
      customBodyRender: (data: unknown) =>
        renderPastDue(data as SubscriptionRow),
    },
  },
  {
    name: "cycles",
    label: "Циклов оплачено",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
    },
  },
  {
    name: "organization",
    label: "Организация",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const subscription = data as SubscriptionRow;
        return (
          <CrudLink
            path={PagePaths.organizations}
            tab="details"
            id={subscription.organizationId}
          >
            {subscription.organization?.title ?? "—"}
          </CrudLink>
        );
      },
    },
  },
];

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Подписки",
  refetchList: "Обновить список",
});

export const getMobileColumnsConfig =
  (): DataListViewColumns<SubscriptionRow> => ({
    main: {
      title: "Подписка",
      sx: { flex: 1, minWidth: 0 },
      renderCell: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <CrudLink
            path={PagePaths.organizations}
            tab="details"
            id={row.organizationId}
            noWrap
          >
            {row.organization?.title ?? "—"}
          </CrudLink>
          <Typography variant="body2" color="textSecondary" noWrap>
            Циклов оплачено: {row.billingCyclesComplete}
          </Typography>
        </Stack>
      ),
    },
    status: {
      title: "Статус",
      sx: { width: 132, flexShrink: 0 },
      renderCell: (row) => renderStatus(row.status),
    },
  });
